import { createStore } from 'vuex'

const emptyOrganization = () => ({
  org_id: '',
  org_name: '',
  org_contact_number: '',
  org_contact_name: '',
  org_country_code: '',
  org_address: '',
  org_city: '',
  org_state: '',
  org_pincode: '',
  org_knowledeg_base: '',
  sip_number: '',
  org_status: '',
  org_contact_email: '',
  org_full_number: '',
  last_updated_on: null,
  created_on: null,
  knowledge_base_synced_on: null,
  org_knowledeg_base_updated: false,
  org_logo_s3_key: '',
  org_knowledge_base_s3_keys: [],
  kb_s3_prefix: '',
  internal_org_id: '',
  org_website_url: '',
  knowledge_base_id: '',
  credits_balance: null,
  total_number_of_calls: null,
  send_call_logs_by_email: false,
  email_addresses: [],
  send_call_logs_by_whatsapp: false,
  whatsapp_numbers: [],
  daily_rental: null,
  voice_agent_cost_per_minute: null,
  chatbot_details: null,
  greeting_message: '',
  system_prompt: '',
  ai_engine: '',
  api_key: '',
  gateway_api_key: '',
  gateway_api_key_id: '',
  oauth_client_id: '',
  oauth_client_secret: '',
  oauth_token_url: '',
})

export default createStore({
  state: {
    tableSearch: '',
    landingOrgSlug: null,
    landingOrganization: emptyOrganization(),
    landingOrgLoaded: false,
    landingOrgLoading: false,
    landingOrgError: '',
    sessionLoaded: false,
    sessionLoading: false,
    sessionError: '',
    currentUserId: null,
    orgId: null,
    userEmail: null,
    emailAddress: null,
    emailChannel: null,
    userName: null,
    userRole: null,
    agentType: null,
    organization: emptyOrganization(),
    isAuthenticated: false,
    authReady: false,
    workspaceReady: false,
    workspaceBootstrapping: false,
    workspaceBootstrapError: '',
    orgDetailsLoading: false,
    orgDetailsLoaded: false,
  },
  getters: {
    isSessionReady: (state) => state.sessionLoaded && !state.sessionLoading,
    sessionContext: (state) => ({
      org_id: state.orgId,
      current_user_id: state.currentUserId,
    }),
    orgDisplayName: (state) =>
      state.organization.org_name || 'Your organisation',
    landingOrgDisplayName: (state) =>
      state.landingOrganization.org_name || 'Yandle Portal',
  },
  mutations: {
    setTableSearch(state, value) {
      state.tableSearch = value ?? ''
    },
    clearTableSearch(state) {
      state.tableSearch = ''
    },
    SET_LANDING_ORG_LOADING(state, loading) {
      state.landingOrgLoading = loading
    },
    SET_LANDING_ORG_ERROR(state, message) {
      state.landingOrgError = message ?? ''
    },
    SET_LANDING_ORG(state, payload) {
      state.landingOrgLoaded = true
      state.landingOrgError = ''
      state.landingOrgSlug = payload.orgSlug ?? null
      state.landingOrganization = {
        ...emptyOrganization(),
        ...payload.organization,
      }
    },
    CLEAR_LANDING_ORG(state) {
      state.landingOrgLoaded = false
      state.landingOrgSlug = null
      state.landingOrganization = emptyOrganization()
      state.landingOrgError = ''
    },
    SET_SESSION_LOADING(state, loading) {
      state.sessionLoading = loading
    },
    SET_SESSION_ERROR(state, message) {
      state.sessionError = message
    },
    SET_SESSION(state, payload) {
      state.sessionLoaded = true
      state.sessionError = ''
      state.currentUserId = payload.currentUserId
      state.orgId = payload.orgId
      state.userEmail = payload.userEmail
      state.emailAddress = payload.emailAddress ?? null
      state.emailChannel = payload.emailChannel ?? null
      state.userName = payload.userName
      state.userRole = payload.userRole ?? null
      state.agentType = payload.agentType ?? null
      const incomingOrgId = payload.organization?.org_id || payload.orgId || ''
      // A refresh of the SAME org's session (e.g. Dashboard's lightweight
      // getCurrentUserDetails re-fetch) must not wipe richer fields already
      // loaded separately via getOrgDetails/UPDATE_ORGANIZATION -- merge on
      // top of the existing organization instead of replacing it. A genuine
      // org/user switch (different org_id, or no prior org loaded) still
      // starts from a clean slate so nothing leaks across accounts.
      const sameOrg =
        !!state.organization?.org_id && state.organization.org_id === incomingOrgId
      state.organization = {
        ...emptyOrganization(),
        ...(sameOrg ? state.organization : {}),
        ...payload.organization,
        org_id: incomingOrgId,
      }
    },
    UPDATE_ORGANIZATION(state, organization) {
      state.organization = { ...state.organization, ...organization }
    },
    SET_ORG_DETAILS_LOADING(state, loading) {
      state.orgDetailsLoading = Boolean(loading)
    },
    SET_ORG_DETAILS_LOADED(state, loaded) {
      state.orgDetailsLoaded = Boolean(loaded)
    },
    SET_AUTH_READY(state, value) {
      state.authReady = Boolean(value)
    },
    SET_AUTHENTICATED(state, value) {
      state.isAuthenticated = Boolean(value)
      if (!state.isAuthenticated) {
        state.workspaceReady = false
        state.workspaceBootstrapping = false
        state.workspaceBootstrapError = ''
      }
    },
    SET_WORKSPACE_BOOTSTRAPPING(state, value) {
      state.workspaceBootstrapping = Boolean(value)
    },
    SET_WORKSPACE_READY(state, value) {
      state.workspaceReady = Boolean(value)
    },
    SET_WORKSPACE_BOOTSTRAP_ERROR(state, message) {
      state.workspaceBootstrapError = message ?? ''
    },
    RESET_WORKSPACE(state) {
      state.workspaceReady = false
      state.workspaceBootstrapping = false
      state.workspaceBootstrapError = ''
    },
    CLEAR_SESSION(state) {
      state.sessionLoaded = false
      state.sessionLoading = false
      state.sessionError = ''
      state.isAuthenticated = false
      state.authReady = true
      state.currentUserId = null
      state.orgId = null
      state.userEmail = null
      state.emailAddress = null
      state.emailChannel = null
      state.userName = null
      state.userRole = null
      state.agentType = null
      state.organization = emptyOrganization()
      state.orgDetailsLoading = false
      state.orgDetailsLoaded = false
      state.workspaceReady = false
      state.workspaceBootstrapping = false
      state.workspaceBootstrapError = ''
    },
  },
})
